import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { TurkeyMap } from "@/components/TurkeyMap";
import { Search, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

const RISK = { Low: "#10B981", Medium: "#F59E0B", High: "#F97316", Critical: "#EF4444" };
const LEVELS = ["", "Low", "Medium", "High", "Critical"];

export default function Registry() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("");

  const load = useCallback(() => {
    setItems(null);
    api.get("/registry", { params: { q, level } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [q, level]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="min-h-screen bg-[#050A15]">
      <Header />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <p className="text-xs uppercase tracking-widest text-[#00FFFF]">On-chain · Transparent</p>
        <h1 className="font-display font-bold text-3xl mt-1">Public Building Safety Registry</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">Every certified inspection is anchored on-chain and searchable by anyone. Turkey focus, TBDY 2018 aligned.</p>

        <div className="qs-panel p-4 mt-6">
          <TurkeyMap points={(items || []).filter((i) => i.lat)} height={340} />
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <div className="flex items-center gap-2 bg-[#0A1128] border border-white/10 px-3 flex-1 min-w-[220px]">
            <Search className="h-4 w-4 text-slate-500" />
            <input data-testid="registry-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search city, project, token…"
              className="bg-transparent py-2.5 w-full outline-none text-sm" />
          </div>
          <div className="flex gap-1.5">
            {LEVELS.map((l) => (
              <button key={l || "all"} data-testid={`filter-${l || "all"}`} onClick={() => setLevel(l)}
                className={`px-3 py-2 text-xs font-mono border transition-colors ${level === l ? "border-[#00FFFF] text-[#00FFFF]" : "border-white/10 text-slate-400"}`}>
                {l || "All"}
              </button>
            ))}
          </div>
        </div>

        <div className="qs-panel mt-4 overflow-x-auto" data-testid="registry-table">
          {items === null ? (
            <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#00FFFF]" /></div>
          ) : items.length === 0 ? (
            <p className="text-center text-slate-500 py-16">No registry entries yet. Run an earthquake inspection to add one.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-white/10">
                  <th className="px-4 py-3">Token</th><th className="px-4 py-3">Project</th><th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">Soil</th><th className="px-4 py-3">Risk</th><th className="px-4 py-3">TBDY</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-slate-500">#{it.certificate_token_id}</td>
                    <td className="px-4 py-3 text-slate-200">{it.project}</td>
                    <td className="px-4 py-3 text-slate-300">{it.city}</td>
                    <td className="px-4 py-3 text-slate-400">{it.soil_class}</td>
                    <td className="px-4 py-3"><span style={{ color: RISK[it.risk_level] }}>{it.risk_level} ({it.risk_score})</span></td>
                    <td className="px-4 py-3">{it.compliant ? <ShieldCheck className="h-4 w-4 text-[#10B981]" /> : <ShieldAlert className="h-4 w-4 text-[#EF4444]" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
